import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { BACKEND_URL, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, LogOut, RefreshCw, ExternalLink, Star, Trash2, Plus, Check, X, Archive, ArchiveRestore, AlertTriangle, Clock } from "lucide-react";
import { formatINR } from "@/data/content";

const STATUS_COLORS = {
  new: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  converted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  lost: "bg-red-500/15 text-red-300 border-red-500/30",
};

const NEXT_ACTION_TYPES = ["Call", "WhatsApp", "Follow up", "Send benchmark", "Send quotation", "Other"];

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [reviews, setReviews] = useState([]);
  const [reviewStatusFilter, setReviewStatusFilter] = useState("all");
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, text: "", car: "", city: "", designation: "" });
  const [cases, setCases] = useState([]);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [caseForm, setCaseForm] = useState({ car: "", city: "", dealer: "", final: "", quote: "", buyer: "", active: true, breakdown: [] });
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userPaidFilter, setUserPaidFilter] = useState("all");
  const [archivedFilter, setArchivedFilter] = useState("active");
  const [tab, setTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [referralStats, setReferralStats] = useState([]);
  const [referralLedger, setReferralLedger] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [suspicious, setSuspicious] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [nextActionDialogOpen, setNextActionDialogOpen] = useState(false);
  const [editingLeadAction, setEditingLeadAction] = useState(null);
  const [nextActionForm, setNextActionForm] = useState({ action: "Call", due_at: "", owner: "" });
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redeemForm, setRedeemForm] = useState({ user_id: "", amount: 1000, note: "" });
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "analytics" && !analytics) loadAnalytics();
    if (tab === "referrals" && referralStats.length === 0) loadReferrals();
    if (tab === "audit" && auditEntries.length === 0) loadAuditLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    reloadLeads(archivedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedFilter]);

  async function loadAnalytics() {
    try {
      const { data } = await api.get("/admin/lead-analytics");
      setAnalytics(data);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to load analytics.");
    }
  }

  async function loadReferrals() {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get("/admin/referrals"),
        api.get("/admin/referrals/ledger"),
        api.get("/admin/referrals/withdrawals"),
        api.get("/admin/referrals/suspicious"),
      ]);
      setReferralStats(r1.data.stats || []);
      setReferralLedger(r2.data.entries || []);
      setWithdrawals(r3.data.withdrawals || []);
      setSuspicious(r4.data.suspicious || []);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to load referrals.");
    }
  }

  async function loadAuditLog() {
    try {
      const { data } = await api.get("/admin/audit-log");
      setAuditEntries(data.entries || []);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to load audit log.");
    }
  }

  async function reloadLeads(nextArchivedFilter) {
    try {
      const { data } = await api.get("/admin/leads", { params: { archived: nextArchivedFilter ?? archivedFilter } });
      setLeads(data.leads || []);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to reload leads.");
    }
  }

  async function archiveLead(id) {
    try {
      await api.post(`/admin/leads/${id}/archive`);
      toast.success("Lead archived");
      reloadLeads();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail)); }
  }

  async function restoreLead(id) {
    try {
      await api.post(`/admin/leads/${id}/restore`);
      toast.success("Lead restored");
      reloadLeads();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail)); }
  }

  async function deleteLeadPermanently(id) {
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead permanently deleted");
      reloadLeads();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail)); }
  }

  function openNextActionEditor(lead) {
    setEditingLeadAction(lead);
    setNextActionForm({
      action: lead.next_action?.action || "Call",
      due_at: lead.next_action?.due_at ? lead.next_action.due_at.slice(0, 16) : "",
      owner: lead.next_action?.owner || "",
    });
    setNextActionDialogOpen(true);
  }

  async function saveNextAction() {
    if (!editingLeadAction) return;
    try {
      const due_at = nextActionForm.due_at ? new Date(nextActionForm.due_at).toISOString() : null;
      await api.patch(`/admin/leads/${editingLeadAction.id}/next-action`, { ...nextActionForm, due_at });
      toast.success("Next action saved");
      setNextActionDialogOpen(false);
      reloadLeads();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail)); }
  }

  async function saveRedemption() {
    try {
      await api.post("/admin/referrals/redeem", redeemForm);
      toast.success("Service credit applied");
      setRedeemDialogOpen(false);
      setRedeemForm({ user_id: "", amount: 1000, note: "" });
      loadReferrals();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail)); }
  }

  async function actOnWithdrawal(id, action) {
    try {
      await api.post(`/admin/referrals/withdrawals/${id}/${action}`, { admin_note: "" });
      toast.success("Updated");
      loadReferrals();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail)); }
  }

  async function load() {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/reviews"),
        api.get("/admin/case-studies"),
      ]);
      setLeads(r1.data.leads || []);
      setStats(r2.data || {});
      setUsers(r3.data.users || []);
      setReviews(r4.data.reviews || []);
      setCases(r5.data.case_studies || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        await logout();
        navigate("/admin");
        return;
      }
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  async function doLogout() {
    await logout();
    navigate("/admin");
  }

  async function updateLeadStatus(id, status) {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Updated");
    } catch { toast.error("Failed"); }
  }

  async function deleteUser(id) {
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success("User deleted");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to delete user");
    }
  }

  function openNewReview() {
    setEditingReview(null);
    setReviewForm({ name: "", rating: 5, text: "", car: "", city: "", designation: "" });
    setReviewDialogOpen(true);
  }

  function openEditReview(r) {
    setEditingReview(r);
    setReviewForm({ name: r.name, rating: r.rating, text: r.text, car: r.car || "", city: r.city || "", designation: r.designation || "" });
    setReviewDialogOpen(true);
  }

  async function saveReview() {
    try {
      if (editingReview) {
        const { data } = await api.patch(`/admin/reviews/${editingReview.id}`, reviewForm);
        setReviews((prev) => prev.map((r) => (r.id === editingReview.id ? data : r)));
        toast.success("Review updated");
      } else {
        const { data } = await api.post("/admin/reviews", reviewForm);
        setReviews((prev) => [data, ...prev]);
        toast.success("Review added");
      }
      setReviewDialogOpen(false);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to save review");
    }
  }

  async function setReviewStatus(id, status) {
    try {
      const { data } = await api.patch(`/admin/reviews/${id}`, { status });
      setReviews((prev) => prev.map((r) => (r.id === id ? data : r)));
    } catch { toast.error("Failed to update status"); }
  }

  async function deleteReview(id) {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch { toast.error("Failed to delete review"); }
  }

  function openNewCase() {
    setEditingCase(null);
    setCaseForm({ car: "", city: "", dealer: "", final: "", quote: "", buyer: "", active: true, breakdown: [] });
    setCaseDialogOpen(true);
  }

  function openEditCase(c) {
    setEditingCase(c);
    setCaseForm({
      car: c.car, city: c.city, dealer: String(c.dealer), final: String(c.final),
      quote: c.quote, buyer: c.buyer, active: c.active, breakdown: c.breakdown || [],
    });
    setCaseDialogOpen(true);
  }

  function updateBreakdownRow(idx, key, value) {
    setCaseForm((f) => {
      const rows = [...f.breakdown];
      rows[idx] = { ...rows[idx], [key]: value };
      return { ...f, breakdown: rows };
    });
  }

  function addBreakdownRow() {
    setCaseForm((f) => ({ ...f, breakdown: [...f.breakdown, { label: "", value: "" }] }));
  }

  function removeBreakdownRow(idx) {
    setCaseForm((f) => ({ ...f, breakdown: f.breakdown.filter((_, i) => i !== idx) }));
  }

  async function saveCase() {
    const payload = {
      ...caseForm,
      dealer: Number(caseForm.dealer) || 0,
      final: Number(caseForm.final) || 0,
      breakdown: caseForm.breakdown.filter((b) => b.label && b.value),
    };
    try {
      if (editingCase) {
        const { data } = await api.patch(`/admin/case-studies/${editingCase.id}`, payload);
        setCases((prev) => prev.map((c) => (c.id === editingCase.id ? data : c)));
        toast.success("Case study updated");
      } else {
        const { data } = await api.post("/admin/case-studies", payload);
        setCases((prev) => [data, ...prev]);
        toast.success("Case study added");
      }
      setCaseDialogOpen(false);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Failed to save case study");
    }
  }

  async function toggleCaseActive(c) {
    try {
      const { data } = await api.patch(`/admin/case-studies/${c.id}`, { active: !c.active });
      setCases((prev) => prev.map((x) => (x.id === c.id ? data : x)));
    } catch { toast.error("Failed to update"); }
  }

  async function deleteCase(id) {
    try {
      await api.delete(`/admin/case-studies/${id}`);
      setCases((prev) => prev.filter((c) => c.id !== id));
      toast.success("Case study deleted");
    } catch { toast.error("Failed to delete case study"); }
  }

  const filteredReviews = useMemo(() => reviews.filter((r) => reviewStatusFilter === "all" || r.status === reviewStatusFilter), [reviews, reviewStatusFilter]);

  function exportCsv() {
    const token = localStorage.getItem("cs_token");
    fetch(`${BACKEND_URL}/api/admin/leads/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "caarsaarthi-leads.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  }

  const filteredLeads = useMemo(() => leads.filter((l) => {
    if (typeFilter !== "all" && l.type !== typeFilter) return false;
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return [l.name, l.phone, l.car, l.city, l.tier].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
    }
    return true;
  }), [leads, typeFilter, statusFilter, q]);

  const filteredUsers = useMemo(() => users.filter((u) => {
    const paid = !!u.deal?.payment;
    if (userPaidFilter === "paid" && !paid) return false;
    if (userPaidFilter === "unpaid" && paid) return false;
    if (q) {
      const s = q.toLowerCase();
      return [u.name, u.email, u.phone, u.deal?.tier].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
    }
    return true;
  }), [users, userPaidFilter, q]);

  const STAT_ITEMS = [
    { k: "Users", v: stats.total_users ?? 0 },
    { k: "Paid users", v: stats.paid_users ?? 0 },
    { k: "Active deals", v: stats.active_deals ?? 0 },
    { k: "Revenue", v: formatINR(stats.revenue ?? 0) },
    { k: "Leads", v: stats.total_leads ?? 0 },
    { k: "WA clicks", v: stats.wa_clicks ?? 0 },
    { k: "Overdue actions", v: stats.overdue_next_actions ?? 0 },
    { k: "Referral risk flags", v: stats.referral_risk_flags ?? 0 },
  ];
  const pendingReviewCount = reviews.filter((r) => r.status === "pending").length;

  return (
    <main className="min-h-screen bg-void text-silver" data-testid="admin-dashboard">
      <header className="border-b border-white/5 sticky top-0 bg-void/85 backdrop-blur-md z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full gradient-bg" />
            <span className="font-serif text-xl tracking-tight text-white">CaarSaarthi · Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 text-silver/70 hover:text-white" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5" data-testid="admin-export-csv"><Download className="w-4 h-4"/> Export Leads</button>
            <button onClick={doLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-white gradient-bg hover:scale-[1.02] transition-transform" data-testid="admin-logout"><LogOut className="w-4 h-4"/> Logout</button>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="admin-stats">
          {STAT_ITEMS.map((s) => (
            <div key={s.k} className="rounded-2xl border border-white/5 bg-surface p-5">
              <div className="text-xs uppercase tracking-widest text-silver/50">{s.k}</div>
              <div className="mt-1 font-serif text-2xl text-white truncate">{s.v}</div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="bg-surface border border-white/10 p-1 rounded-full">
            <TabsTrigger value="users" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="tab-users">Users & Deals</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="tab-reviews">
              Reviews {pendingReviewCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-[10px] text-black font-bold">{pendingReviewCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="cases" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="tab-cases">Case Studies</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="referrals" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="tab-referrals">
              Referrals {suspicious.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-[10px] text-white font-bold">{suspicious.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="audit" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="tab-audit">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone…" className="bg-surface border-white/10 h-11 text-white md:max-w-sm" data-testid="admin-user-search" />
              <Select value={userPaidFilter} onValueChange={setUserPaidFilter}>
                <SelectTrigger className="bg-surface border-white/10 h-11 text-white md:w-48" data-testid="admin-user-paid-filter"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="bg-surface text-silver border-white/10">
                  <SelectItem value="all">All users</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-6 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-users-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>User</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-silver/60">Loading…</TableCell></TableRow>
                  ) : filteredUsers.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-silver/60">No users yet.</TableCell></TableRow>
                  ) : (
                    filteredUsers.map((u) => (
                      <TableRow key={u.id} className="border-white/5" data-testid={`user-row-${u.id}`}>
                        <TableCell className="text-white">
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-silver/50">{u.email}</div>
                        </TableCell>
                        <TableCell>{u.phone}</TableCell>
                        <TableCell>{u.deal?.tier ? <Badge className="gradient-bg text-white border-0">{u.deal.tier}</Badge> : u.deal?.requested_tier ? <Badge variant="outline" className="border-[#8B5CF6]/40 text-[#C4B5FD]" data-testid={`requested-tier-${u.id}`}>Requested: {u.deal.requested_tier}</Badge> : <span className="text-silver/40 text-xs">—</span>}</TableCell>
                        <TableCell className="text-xs text-silver/80">{u.deal?.stage || "AWAITING_PAYMENT"}</TableCell>
                        <TableCell>
                          {u.deal?.payment ? (
                            <div>
                              <div className="text-emerald-300">{formatINR(u.deal.payment.amount)}</div>
                              <div className="text-xs text-silver/50">{u.deal.payment.mode} · {u.deal.payment.date}</div>
                            </div>
                          ) : (
                            <Badge variant="outline" className="border-amber-500/40 text-amber-300">Unpaid</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-silver/60 text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Link to={`/admin/users/${u.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5" data-testid={`user-open-${u.id}`}>
                              Open <ExternalLink className="w-3 h-3" />
                            </Link>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid={`user-delete-${u.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-surface border-white/10 text-silver">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">Delete {u.name}?</AlertDialogTitle>
                                  <AlertDialogDescription className="text-silver/60">
                                    This permanently deletes their account, deal, documents, messages and PDI sessions. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-transparent border-white/10 text-silver hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteUser(u.id)} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`user-delete-confirm-${u.id}`}>
                                    Delete permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, car…" className="bg-surface border-white/10 h-11 text-white md:max-w-sm" data-testid="admin-search" />
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="bg-surface border-white/10 h-11 text-white md:w-48" data-testid="admin-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent className="bg-surface text-silver border-white/10">
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="consultation">Consultations</SelectItem>
                  <SelectItem value="checklist">Checklist</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-surface border-white/10 h-11 text-white md:w-48" data-testid="admin-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent className="bg-surface text-silver border-white/10">
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              <Select value={archivedFilter} onValueChange={setArchivedFilter}>
                <SelectTrigger className="bg-surface border-white/10 h-11 text-white md:w-48" data-testid="admin-archived-filter"><SelectValue placeholder="Active leads" /></SelectTrigger>
                <SelectContent className="bg-surface text-silver border-white/10">
                  <SelectItem value="active">Active leads</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="all">All leads</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-6 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-leads-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Car</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Next action</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8 text-silver/60">No leads.</TableCell></TableRow>
                  ) : (
                    filteredLeads.map((l) => {
                      const overdue = l.next_action?.due_at && new Date(l.next_action.due_at) < new Date() && !l.archived;
                      return (
                        <TableRow key={l.id} className="border-white/5" data-testid={`lead-row-${l.id}`}>
                          <TableCell className="text-white">
                            <div className="flex items-center gap-2">
                              {l.name}
                              {l.is_duplicate && (
                                <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px] gap-1" data-testid={`lead-duplicate-${l.id}`}>
                                  <AlertTriangle className="w-3 h-3" /> Duplicate
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{l.phone}</TableCell>
                          <TableCell><Badge variant="outline" className="border-white/10 text-silver/80 capitalize">{l.type}</Badge></TableCell>
                          <TableCell><span className="text-xs text-silver/70 capitalize">{l.source || "website"}</span></TableCell>
                          <TableCell>{l.car || "—"}</TableCell>
                          <TableCell>{l.city || "—"}</TableCell>
                          <TableCell>
                            <Select value={l.status} onValueChange={(v) => updateLeadStatus(l.id, v)}>
                              <SelectTrigger className={`h-8 rounded-full border ${STATUS_COLORS[l.status] || ""} text-xs w-32`} data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-surface text-silver border-white/10">
                                <SelectItem value="new">new</SelectItem>
                                <SelectItem value="contacted">contacted</SelectItem>
                                <SelectItem value="converted">converted</SelectItem>
                                <SelectItem value="lost">lost</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => openNextActionEditor(l)}
                              className={`text-xs text-left rounded-lg px-2.5 py-1.5 border ${overdue ? "border-red-500/40 text-red-300 bg-red-500/10" : "border-white/10 text-silver/70 hover:bg-white/5"}`}
                              data-testid={`lead-next-action-${l.id}`}
                            >
                              {l.next_action ? (
                                <>
                                  <div className="font-medium flex items-center gap-1">{overdue && <Clock className="w-3 h-3" />}{l.next_action.action}</div>
                                  <div className="text-[10px] opacity-70">{l.next_action.due_at ? new Date(l.next_action.due_at).toLocaleString() : "No due date"} · {l.next_action.owner}</div>
                                </>
                              ) : "Set next action"}
                            </button>
                          </TableCell>
                          <TableCell className="text-silver/60 text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {!l.archived ? (
                                <button onClick={() => archiveLead(l.id)} className="p-2 rounded-full border border-white/10 hover:bg-white/5 text-silver/70" title="Archive" data-testid={`lead-archive-${l.id}`}>
                                  <Archive className="w-3.5 h-3.5" />
                                </button>
                              ) : (
                                <>
                                  <button onClick={() => restoreLead(l.id)} className="p-2 rounded-full border border-white/10 hover:bg-white/5 text-silver/70" title="Restore" data-testid={`lead-restore-${l.id}`}>
                                    <ArchiveRestore className="w-3.5 h-3.5" />
                                  </button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button className="p-2 rounded-full border border-red-500/20 text-red-400 hover:bg-red-500/10" title="Delete permanently" data-testid={`lead-delete-${l.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-surface border-white/10 text-silver">
                                      <AlertDialogHeader>
                                        <AlertDialogTitle className="text-white">Permanently delete this lead?</AlertDialogTitle>
                                        <AlertDialogDescription className="text-silver/60">
                                          Use this only for spam, junk, or data-removal requests. This cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel className="bg-transparent border-white/10 text-silver hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => deleteLeadPermanently(l.id)} className="bg-red-600 hover:bg-red-700 text-white">
                                          Delete permanently
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            {!analytics ? (
              <div className="text-center py-8 text-silver/60">Loading analytics…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-2xl border border-white/5 bg-surface p-5">
                    <div className="text-xs uppercase tracking-widest text-silver/50">Leads</div>
                    <div className="mt-1 font-serif text-2xl text-white">{analytics.totals.leads}</div>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-surface p-5">
                    <div className="text-xs uppercase tracking-widest text-silver/50">Consultations</div>
                    <div className="mt-1 font-serif text-2xl text-white">{analytics.totals.consultations}</div>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-surface p-5">
                    <div className="text-xs uppercase tracking-widest text-silver/50">Closed customers</div>
                    <div className="mt-1 font-serif text-2xl text-white">{analytics.totals.closed_customers}</div>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-surface p-5">
                    <div className="text-xs uppercase tracking-widest text-silver/50">Attributed revenue</div>
                    <div className="mt-1 font-serif text-2xl text-white">{formatINR(analytics.totals.revenue)}</div>
                  </div>
                </div>
                <div className="mt-6 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-analytics-table">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead>Source</TableHead>
                        <TableHead>Leads</TableHead>
                        <TableHead>Consultations</TableHead>
                        <TableHead>Converted</TableHead>
                        <TableHead>Duplicates</TableHead>
                        <TableHead>Closed customers</TableHead>
                        <TableHead>Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.sources.map((s) => (
                        <TableRow key={s.source} className="border-white/5">
                          <TableCell className="text-white capitalize">{s.source}</TableCell>
                          <TableCell>{s.leads}</TableCell>
                          <TableCell>{s.consultations}</TableCell>
                          <TableCell>{s.converted}</TableCell>
                          <TableCell>{s.duplicates}</TableCell>
                          <TableCell>{s.closed_customers}</TableCell>
                          <TableCell>{formatINR(s.revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-3 text-xs text-silver/40">{analytics.note}</p>
              </>
            )}
          </TabsContent>

          <TabsContent value="referrals" className="mt-6">
            {suspicious.length > 0 && (
              <div className="mb-6 rounded-2xl border border-red-500/20 bg-red-500/5 p-5">
                <h4 className="text-sm font-medium text-red-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Suspicious referrals</h4>
                <div className="mt-3 space-y-2">
                  {suspicious.map((s) => (
                    <div key={s.referred_user_id} className="text-xs text-silver/70 flex flex-wrap items-center gap-2">
                      <span className="text-white">{s.referred_name}</span> ({s.referred_email}) referred by{" "}
                      <span className="text-white">{s.referrer?.name || "—"}</span> ({s.referrer?.email || "—"}) ·{" "}
                      {s.risk_flags.map((f) => <Badge key={f} variant="outline" className="border-red-500/40 text-red-300 ml-1">{f}</Badge>)} ·{" "}
                      {new Date(s.signup_time).toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-white">Referrer ledger</h4>
              <button onClick={() => setRedeemDialogOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5" data-testid="referral-redeem-open">
                <Plus className="w-4 h-4" /> Apply service credit
              </button>
            </div>
            <div className="mt-3 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-referral-stats-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Referrer</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Sign-ups</TableHead>
                    <TableHead>Successful</TableHead>
                    <TableHead>Remaining slots</TableHead>
                    <TableHead>Earned</TableHead>
                    <TableHead>Service used</TableHead>
                    <TableHead>Withdrawn</TableHead>
                    <TableHead>Pending</TableHead>
                    <TableHead>Available</TableHead>
                    <TableHead>Suspicious</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralStats.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-silver/60">No referral activity yet.</TableCell></TableRow>
                  ) : (
                    referralStats.map((r) => (
                      <TableRow key={r.user_id} className="border-white/5">
                        <TableCell className="text-white">{r.name}<div className="text-xs text-silver/50">{r.email}</div></TableCell>
                        <TableCell className="font-mono text-xs">{r.code}</TableCell>
                        <TableCell>{r.signups}</TableCell>
                        <TableCell>{r.successful_referrals}/5</TableCell>
                        <TableCell>{r.remaining_slots}</TableCell>
                        <TableCell>{formatINR(r.credits_earned)}</TableCell>
                        <TableCell>{formatINR(r.service_redeemed)}</TableCell>
                        <TableCell>{formatINR(r.withdrawn)}</TableCell>
                        <TableCell>{formatINR(r.pending_withdrawal)}</TableCell>
                        <TableCell className="text-white">{formatINR(r.available_credit)}</TableCell>
                        <TableCell>{r.suspicious_referrals > 0 ? <Badge variant="outline" className="border-red-500/40 text-red-300">{r.suspicious_referrals}</Badge> : "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <h4 className="mt-8 text-sm font-medium text-white">Payout requests</h4>
            <div className="mt-3 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-withdrawals-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>User</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-silver/60">No payout requests.</TableCell></TableRow>
                  ) : (
                    withdrawals.map((w) => (
                      <TableRow key={w.id} className="border-white/5">
                        <TableCell className="text-white text-xs">{w.user_id}</TableCell>
                        <TableCell>{formatINR(w.amount)}</TableCell>
                        <TableCell><Badge variant="outline" className="border-white/10 text-silver/80 capitalize">{w.status}</Badge></TableCell>
                        <TableCell className="text-xs text-silver/60">{new Date(w.requested_at).toLocaleString()}</TableCell>
                        <TableCell>
                          {w.status === "requested" && (
                            <div className="flex gap-1.5">
                              <button onClick={() => actOnWithdrawal(w.id, "approve")} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5">Approve</button>
                              <button onClick={() => actOnWithdrawal(w.id, "reject")} className="text-xs px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10">Reject</button>
                            </div>
                          )}
                          {w.status === "approved" && (
                            <button onClick={() => actOnWithdrawal(w.id, "mark-paid")} className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">Mark paid</button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <h4 className="mt-8 text-sm font-medium text-white">Ledger</h4>
            <div className="mt-3 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-referral-ledger-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Type</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralLedger.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-silver/60">No ledger entries yet.</TableCell></TableRow>
                  ) : (
                    referralLedger.map((e, i) => (
                      <TableRow key={i} className="border-white/5">
                        <TableCell className="capitalize">{e.type}</TableCell>
                        <TableCell className="text-xs">{e.user_id}</TableCell>
                        <TableCell className={e.amount < 0 ? "text-red-300" : "text-emerald-300"}>{e.amount < 0 ? "-" : "+"}{formatINR(Math.abs(e.amount))}</TableCell>
                        <TableCell className="capitalize">{e.status}</TableCell>
                        <TableCell className="text-xs text-silver/60">{e.date ? new Date(e.date).toLocaleString() : "—"}</TableCell>
                        <TableCell className="text-xs text-silver/60">{e.note}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <div className="rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-audit-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Time</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditEntries.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-silver/60">No audit events yet.</TableCell></TableRow>
                  ) : (
                    auditEntries.map((a) => (
                      <TableRow key={a.id} className="border-white/5">
                        <TableCell className="text-xs text-silver/60">{new Date(a.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-white text-xs">{a.actor_name}</TableCell>
                        <TableCell className="text-xs"><Badge variant="outline" className="border-white/10 text-silver/80">{a.action}</Badge></TableCell>
                        <TableCell className="text-xs text-silver/60">{a.target_type} · {a.target_id?.slice(0, 8)}</TableCell>
                        <TableCell className="text-xs text-silver/60">{a.details}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
              <Select value={reviewStatusFilter} onValueChange={setReviewStatusFilter}>
                <SelectTrigger className="bg-surface border-white/10 h-11 text-white md:w-48" data-testid="admin-review-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="bg-surface text-silver border-white/10">
                  <SelectItem value="all">All reviews</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={openNewReview} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm text-white gradient-bg hover:scale-[1.02] transition-transform" data-testid="admin-add-review">
                <Plus className="w-4 h-4" /> Add Review
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-reviews-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead>Car</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReviews.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-silver/60">No reviews.</TableCell></TableRow>
                  ) : (
                    filteredReviews.map((r) => (
                      <TableRow key={r.id} className="border-white/5" data-testid={`review-row-${r.id}`}>
                        <TableCell className="text-white">{r.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-0.5 text-[#F43F5E]">
                            {Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-[#F43F5E]" />)}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-sm text-xs text-silver/80">{r.text}</TableCell>
                        <TableCell className="text-xs">{r.car || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className="border-white/10 text-silver/70 capitalize text-[10px]">{r.source}</Badge></TableCell>
                        <TableCell>
                          <Badge className={
                            r.status === "approved" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
                            r.status === "rejected" ? "bg-red-500/15 text-red-300 border-red-500/30" :
                            "bg-amber-500/15 text-amber-300 border-amber-500/30"
                          }>{r.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {r.status !== "approved" && (
                              <button onClick={() => setReviewStatus(r.id, "approved")} className="w-8 h-8 grid place-items-center rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" data-testid={`review-approve-${r.id}`}>
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {r.status !== "rejected" && (
                              <button onClick={() => setReviewStatus(r.id, "rejected")} className="w-8 h-8 grid place-items-center rounded-full border border-white/10 text-silver/60 hover:bg-white/5" data-testid={`review-reject-${r.id}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => openEditReview(r)} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5" data-testid={`review-edit-${r.id}`}>Edit</button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button className="w-8 h-8 grid place-items-center rounded-full border border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid={`review-delete-${r.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-surface border-white/10 text-silver">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">Delete this review?</AlertDialogTitle>
                                  <AlertDialogDescription className="text-silver/60">This cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-transparent border-white/10 text-silver hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteReview(r.id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="cases" className="mt-6">
            <div className="flex justify-end">
              <button onClick={openNewCase} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm text-white gradient-bg hover:scale-[1.02] transition-transform" data-testid="admin-add-case">
                <Plus className="w-4 h-4" /> Add Case Study
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-white/5 bg-surface overflow-x-auto" data-testid="admin-cases-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Car</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Dealer Quote</TableHead>
                    <TableHead>Final Price</TableHead>
                    <TableHead>Saved</TableHead>
                    <TableHead>Buyer</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cases.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-silver/60">No case studies.</TableCell></TableRow>
                  ) : (
                    cases.map((c) => (
                      <TableRow key={c.id} className="border-white/5" data-testid={`case-row-${c.id}`}>
                        <TableCell className="text-white">{c.car}</TableCell>
                        <TableCell className="text-xs">{c.city}</TableCell>
                        <TableCell className="text-xs text-silver/60 line-through">{formatINR(c.dealer)}</TableCell>
                        <TableCell className="text-xs">{formatINR(c.final)}</TableCell>
                        <TableCell className="text-xs text-emerald-300">{formatINR(c.saved)}</TableCell>
                        <TableCell className="text-xs">{c.buyer}</TableCell>
                        <TableCell>
                          <button onClick={() => toggleCaseActive(c)} data-testid={`case-toggle-${c.id}`}>
                            <Badge className={c.active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-silver/50 border-white/10"}>
                              {c.active ? "Live" : "Hidden"}
                            </Badge>
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => openEditCase(c)} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5" data-testid={`case-edit-${c.id}`}>Edit</button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button className="w-8 h-8 grid place-items-center rounded-full border border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid={`case-delete-${c.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-surface border-white/10 text-silver">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">Delete this case study?</AlertDialogTitle>
                                  <AlertDialogDescription className="text-silver/60">This cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-transparent border-white/10 text-silver hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteCase(c.id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </section>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-surface border-white/10 text-silver max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-serif text-2xl">{editingReview ? "Edit review" : "Add review"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Name</label>
              <Input value={reviewForm.name} onChange={(e) => setReviewForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-silver/50 uppercase tracking-wider">Car</label>
                <Input value={reviewForm.car} onChange={(e) => setReviewForm((f) => ({ ...f, car: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-form-car" />
              </div>
              <div>
                <label className="text-xs text-silver/50 uppercase tracking-wider">City</label>
                <Input value={reviewForm.city} onChange={(e) => setReviewForm((f) => ({ ...f, city: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-form-city" />
              </div>
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Designation (optional)</label>
              <Input value={reviewForm.designation} onChange={(e) => setReviewForm((f) => ({ ...f, designation: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-form-designation" />
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Rating</label>
              <div className="mt-1.5 flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setReviewForm((f) => ({ ...f, rating: n }))} data-testid={`review-form-star-${n}`}>
                    <Star className={`w-6 h-6 ${n <= reviewForm.rating ? "fill-[#F43F5E] text-[#F43F5E]" : "text-silver/25"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Review text</label>
              <Textarea value={reviewForm.text} onChange={(e) => setReviewForm((f) => ({ ...f, text: e.target.value }))} rows={4} className="mt-1.5 bg-void border-white/10 text-white" data-testid="review-form-text" />
            </div>
            <button onClick={saveReview} className="w-full py-3 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.01] transition-transform" data-testid="review-form-save">
              {editingReview ? "Save changes" : "Add review"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={caseDialogOpen} onOpenChange={setCaseDialogOpen}>
        <DialogContent className="bg-surface border-white/10 text-silver max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-serif text-2xl">{editingCase ? "Edit case study" : "Add case study"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-silver/50 uppercase tracking-wider">Car</label>
                <Input value={caseForm.car} onChange={(e) => setCaseForm((f) => ({ ...f, car: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="case-form-car" />
              </div>
              <div>
                <label className="text-xs text-silver/50 uppercase tracking-wider">City</label>
                <Input value={caseForm.city} onChange={(e) => setCaseForm((f) => ({ ...f, city: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="case-form-city" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-silver/50 uppercase tracking-wider">Dealer quote (₹)</label>
                <Input type="number" value={caseForm.dealer} onChange={(e) => setCaseForm((f) => ({ ...f, dealer: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="case-form-dealer" />
              </div>
              <div>
                <label className="text-xs text-silver/50 uppercase tracking-wider">Final price (₹)</label>
                <Input type="number" value={caseForm.final} onChange={(e) => setCaseForm((f) => ({ ...f, final: e.target.value }))} className="mt-1.5 bg-void border-white/10 text-white" data-testid="case-form-final" />
              </div>
            </div>
            <p className="text-xs text-silver/40">
              Saved amount is calculated automatically: {formatINR(Math.max((Number(caseForm.dealer) || 0) - (Number(caseForm.final) || 0), 0))}
            </p>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Buyer</label>
              <Input value={caseForm.buyer} onChange={(e) => setCaseForm((f) => ({ ...f, buyer: e.target.value }))} placeholder="e.g. Rohan M., Founder" className="mt-1.5 bg-void border-white/10 text-white" data-testid="case-form-buyer" />
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Quote</label>
              <Textarea value={caseForm.quote} onChange={(e) => setCaseForm((f) => ({ ...f, quote: e.target.value }))} rows={2} className="mt-1.5 bg-void border-white/10 text-white" data-testid="case-form-quote" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs text-silver/50 uppercase tracking-wider">Savings breakdown</label>
                <button type="button" onClick={addBreakdownRow} className="text-xs text-[#8B5CF6] hover:text-white" data-testid="case-form-add-breakdown">+ Add line</button>
              </div>
              <div className="mt-2 space-y-2">
                {caseForm.breakdown.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={b.label} onChange={(e) => updateBreakdownRow(i, "label", e.target.value)} placeholder="Label" className="bg-void border-white/10 text-white text-sm" data-testid={`case-breakdown-label-${i}`} />
                    <Input value={b.value} onChange={(e) => updateBreakdownRow(i, "value", e.target.value)} placeholder="₹Value" className="bg-void border-white/10 text-white text-sm w-28" data-testid={`case-breakdown-value-${i}`} />
                    <button type="button" onClick={() => removeBreakdownRow(i)} className="text-silver/40 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-silver/70">
              <input type="checkbox" checked={caseForm.active} onChange={(e) => setCaseForm((f) => ({ ...f, active: e.target.checked }))} data-testid="case-form-active" />
              Live on the homepage
            </label>
            <button onClick={saveCase} className="w-full py-3 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.01] transition-transform" data-testid="case-form-save">
              {editingCase ? "Save changes" : "Add case study"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={nextActionDialogOpen} onOpenChange={setNextActionDialogOpen}>
        <DialogContent className="bg-surface border-white/10 text-silver max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-serif text-2xl">Next action</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Action</label>
              <Select value={nextActionForm.action} onValueChange={(v) => setNextActionForm((f) => ({ ...f, action: v }))}>
                <SelectTrigger className="mt-1.5 bg-void border-white/10 text-white" data-testid="next-action-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface text-silver border-white/10">
                  {NEXT_ACTION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Due date & time</label>
              <Input
                type="datetime-local"
                value={nextActionForm.due_at}
                onChange={(e) => setNextActionForm((f) => ({ ...f, due_at: e.target.value }))}
                className="mt-1.5 bg-void border-white/10 text-white"
                data-testid="next-action-due"
              />
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Owner</label>
              <Input
                value={nextActionForm.owner}
                onChange={(e) => setNextActionForm((f) => ({ ...f, owner: e.target.value }))}
                placeholder="Defaults to you"
                className="mt-1.5 bg-void border-white/10 text-white"
                data-testid="next-action-owner"
              />
            </div>
            <button onClick={saveNextAction} className="w-full py-3 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.01] transition-transform" data-testid="next-action-save">
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={redeemDialogOpen} onOpenChange={setRedeemDialogOpen}>
        <DialogContent className="bg-surface border-white/10 text-silver max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-serif text-2xl">Apply service credit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">User ID</label>
              <Input
                value={redeemForm.user_id}
                onChange={(e) => setRedeemForm((f) => ({ ...f, user_id: e.target.value }))}
                placeholder="Paste the user's ID from the Users tab"
                className="mt-1.5 bg-void border-white/10 text-white"
                data-testid="redeem-user-id"
              />
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Amount (₹1,000 increments)</label>
              <Input
                type="number"
                step={1000}
                min={1000}
                value={redeemForm.amount}
                onChange={(e) => setRedeemForm((f) => ({ ...f, amount: Number(e.target.value) }))}
                className="mt-1.5 bg-void border-white/10 text-white"
                data-testid="redeem-amount"
              />
            </div>
            <div>
              <label className="text-xs text-silver/50 uppercase tracking-wider">Note</label>
              <Textarea
                value={redeemForm.note}
                onChange={(e) => setRedeemForm((f) => ({ ...f, note: e.target.value }))}
                rows={2}
                className="mt-1.5 bg-void border-white/10 text-white"
                data-testid="redeem-note"
              />
            </div>
            <button onClick={saveRedemption} className="w-full py-3 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.01] transition-transform" data-testid="redeem-save">
              Apply credit
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
